import { cn } from "@/components/utils";
import { cva, VariantProps } from "class-variance-authority";
import React from "react";

const bottomNavVariants = cva(
  "flex h-14 items-center justify-center px-2 shadow-t",
  {
    variants: {
      varient: {
        default: "bg-card [&>*]:text-subline [&>.selected]:text-secondary",
        primary: "bg-primary [&>*]:text-white/70 [&>.selected]:text-sky-400",
        secondary: "bg-secondary [&>*]:text-white/70 [&>.selected]:text-white",
      },
      position: {
        fixed: "fixed bottom-0 left-0 right-0 z-10",
      },
    },
    defaultVariants: {
      varient: "default",
      position: "fixed",
    },
  }
);
export interface BottomNavProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof bottomNavVariants> {}
/**
 * Bottom app bar container
 * @returns BottomNavBar JSX
 */
const BottomNavBar = React.forwardRef<HTMLDivElement, BottomNavProps>(
  ({ className, varient, position, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(bottomNavVariants({ className, varient, position }))}
      {...props}
    />
  )
);

BottomNavBar.displayName = "BottomNavBar";

interface NavButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /* use only lucide icons https://lucide.dev/icons/ */
  icon: React.ReactElement;
  /** Menu label string */
  label?: string;
  /** Selected menu boolean */
  selected?: boolean;
  iconStyle?: string;
}

/**
 * use only lucide icons https://lucide.dev/icons/
 * and onClick event
 * @returns Bottom nav action button JSX
 */
const BottomNavBarAction = React.forwardRef<HTMLButtonElement, NavButtonProps>(
  ({ className, icon, label, selected, iconStyle, ...props }, ref) => {
    return (
      <button
        className={cn(
          `ripple inline-flex h-full min-w-20 max-w-40 flex-1 flex-col items-center justify-center px-3 ${selected ? "selected" : ""}`,
          className
        )}
        ref={ref}
        tabIndex={0}
        {...props}
      >
        <span
          className={`mb-1 flex items-center justify-center ${iconStyle || ""}`}
        >
          {React.cloneElement(icon, { strokeWidth: 2, size: 18 })}
        </span>
        {label ? <span>{label}</span> : null}
      </button>
    );
  }
);

BottomNavBarAction.displayName = "BottomNavBarAction";

export { BottomNavBar, BottomNavBarAction };
